import { formatearMoneda, type ServicioVista } from '@barbones/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Scissors, Settings2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { api, mensajeError } from '@/api/cliente';
import { useSesion } from '@/api/sesion';
import { EncabezadoPagina, ModuleLayout } from '@/components/layout/ModuleLayout';
import { SECCIONES_POR_MODULO } from '@/components/layout/secciones';
import { Button } from '@/components/ui/button';
import { Esqueleto, Insignia, Tabla, Tarjeta, Td, Th, Tr, Vacio } from '@/components/ui/data';
import { Campo, Input, Select, Switch } from '@/components/ui/form';
import { Dialogo } from '@/components/ui/overlay';
import { useBarberos, useServicios } from '../agenda/datos';
import { avisarError } from '@/lib/avisos';

function DialogoServicio({ servicio, onCerrar }: { servicio?: ServicioVista; onCerrar: () => void }) {
  const qc = useQueryClient();
  const barberos = useBarberos();
  const [nombre, setNombre] = useState(servicio?.nombre ?? '');
  const [precio, setPrecio] = useState(servicio ? String(servicio.precio / 100) : '');
  const [activo, setActivo] = useState(servicio?.activo ?? true);
  const [confs, setConfs] = useState<Record<string, { duracion: string; tipo: 'porcentaje' | 'fijo'; valor: string }>>(() =>
    Object.fromEntries(
      (servicio?.barberos ?? []).map((b) => [
        b.barberoId,
        { duracion: String(b.duracionMin), tipo: b.comisionTipo, valor: String(b.comisionTipo === 'porcentaje' ? b.comisionValor / 100 : b.comisionValor / 100) },
      ]),
    ),
  );

  const guardar = useMutation({
    mutationFn: async () => {
      const body = { nombre, precio: Math.round(Number.parseFloat(precio || '0') * 100), activo };
      const s = servicio
        ? await api<ServicioVista>(`/servicios/${servicio.id}`, { method: 'PATCH', body })
        : await api<ServicioVista>('/servicios', { method: 'POST', body });
      for (const [barberoId, c] of Object.entries(confs)) {
        if (!c.duracion) continue;
        await api(`/servicios/${s.id}/barberos/${barberoId}`, {
          method: 'PUT',
          body: { duracionMin: Number(c.duracion), comisionTipo: c.tipo, comisionValor: Math.round(Number.parseFloat(c.valor || '0') * 100) },
        });
      }
    },
    onSuccess: () => {
      toast.success('Servicio guardado');
      void qc.invalidateQueries({ queryKey: ['servicios'] });
      onCerrar();
    },
    onError: (e) => avisarError(e),
  });

  return (
    <Dialogo
      abierto
      ancho="lg"
      onCambiarAbierto={(v) => !v && onCerrar()}
      titulo={servicio ? servicio.nombre : 'Nuevo servicio'}
      descripcion="El precio es el mismo para todos; la duración y la comisión dependen del barbero."
      pie={
        <>
          <Button variante="secundario" onClick={onCerrar}>
            Cancelar
          </Button>
          <Button onClick={() => guardar.mutate()} cargando={guardar.isPending} disabled={!nombre}>
            Guardar
          </Button>
        </>
      }
    >
      <div className="grid gap-4 sm:grid-cols-3">
        <Campo etiqueta="Nombre" requerido className="sm:col-span-2">
          {(p) => <Input {...p} value={nombre} onChange={(e) => setNombre(e.target.value)} />}
        </Campo>
        <Campo etiqueta="Precio (MXN)" requerido>
          {(p) => <Input {...p} type="number" min="0" step="0.01" value={precio} onChange={(e) => setPrecio(e.target.value)} className="cifras" />}
        </Campo>
        <label className="flex items-center gap-2 text-[13px] sm:col-span-3">
          <Switch checked={activo} onCheckedChange={setActivo} /> Servicio activo (se puede agendar)
        </label>
      </div>
      <h3 className="mb-2 mt-5 text-[16px]">Por barbero</h3>
      <div className="flex flex-col gap-2">
        {barberos.data?.map((b) => {
          const c = confs[b.id] ?? { duracion: '', tipo: 'porcentaje' as const, valor: '' };
          const set = (cambios: Partial<typeof c>) => setConfs((prev) => ({ ...prev, [b.id]: { ...c, ...cambios } }));
          return (
            <div key={b.id} className="grid items-end gap-2 rounded-lg border border-border p-3 sm:grid-cols-[1fr_120px_140px_120px]">
              <p className="self-center font-medium">{b.nombre}</p>
              <Campo etiqueta="Duración (min)">
                {(p) => <Input {...p} type="number" min="5" step="5" value={c.duracion} onChange={(e) => set({ duracion: e.target.value })} className="cifras" />}
              </Campo>
              <Campo etiqueta="Comisión">
                {(p) => (
                  <Select {...p} value={c.tipo} onChange={(e) => set({ tipo: e.target.value as 'porcentaje' | 'fijo' })}>
                    <option value="porcentaje">Porcentaje</option>
                    <option value="fijo">Monto fijo</option>
                  </Select>
                )}
              </Campo>
              <Campo etiqueta={c.tipo === 'porcentaje' ? '%' : 'MXN'}>
                {(p) => <Input {...p} type="number" min="0" step="0.01" value={c.valor} onChange={(e) => set({ valor: e.target.value })} className="cifras" />}
              </Campo>
            </div>
          );
        })}
      </div>
    </Dialogo>
  );
}

export function ServiciosPage() {
  const { puede } = useSesion();
  const servicios = useServicios(false);
  const barberos = useBarberos();
  const [editar, setEditar] = useState<ServicioVista | 'nuevo' | null>(null);

  return (
    <ModuleLayout titulo="Configuración" secciones={SECCIONES_POR_MODULO.configuracion}>
      <EncabezadoPagina
        titulo="Servicios"
        descripcion="Catálogo de servicios, precio y duración/comisión por barbero."
        acciones={
          puede('config.editar') && (
            <Button onClick={() => setEditar('nuevo')}>
              <Plus /> Nuevo servicio
            </Button>
          )
        }
      />
      <Tarjeta>
        {servicios.isLoading ? (
          <Esqueleto className="m-4 h-32" />
        ) : !servicios.data?.length ? (
          <Vacio icono={<Scissors />} titulo="Sin servicios" />
        ) : (
          <Tabla>
            <thead>
              <tr>
                <Th>Servicio</Th>
                <Th className="text-right">Precio</Th>
                <Th>Duración por barbero</Th>
                <Th>Estado</Th>
                <Th className="w-12" />
              </tr>
            </thead>
            <tbody>
              {servicios.data.map((s) => (
                <Tr key={s.id}>
                  <Td className="font-medium">{s.nombre}</Td>
                  <Td className="cifras text-right">{s.precio ? formatearMoneda(s.precio) : <span className="text-warning">Por definir</span>}</Td>
                  <Td className="text-[12.5px] text-foreground-muted">
                    {s.barberos.length === 0
                      ? 'Sin configurar'
                      : s.barberos.map((b) => `${barberos.data?.find((x) => x.id === b.barberoId)?.nombre ?? '—'}: ${b.duracionMin} min`).join(' · ')}
                  </Td>
                  <Td>
                    <Insignia tono={s.activo ? 'exito' : 'neutro'}>{s.activo ? 'Activo' : 'Inactivo'}</Insignia>
                  </Td>
                  <Td>
                    {puede('config.editar') && (
                      <Button variante="fantasma" tamano="icono" onClick={() => setEditar(s)} aria-label={`Editar ${s.nombre}`}>
                        <Settings2 />
                      </Button>
                    )}
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Tabla>
        )}
      </Tarjeta>
      {editar && <DialogoServicio servicio={editar === 'nuevo' ? undefined : editar} onCerrar={() => setEditar(null)} />}
    </ModuleLayout>
  );
}
